<template>
    <v-app>
        <div class="demo-wrapper">
            <backdrop-demo />

            <step-navigation
                v-if="steps.length > 0"
                :steps="steps"
                :current_step_number="current_step_number"
                @step-click="$emit('step-click', $event)"
            />

            <v-row class="content-card">
                <v-col
                    cols="12"
                    md="6"
                    class="pt-md-6"
                >
                    <transition name="explainer-fade" mode="out-in">
                        <div :key="browser_content_key">
                            <slot name="explainer" :business_name="business_name" />
                        </div>
                    </transition>
                </v-col>

                <v-col
                    cols="12"
                    md="6"
                >
                    <mock-phone
                        :key="phone_key"
                        :business_name="business_name"
                        :phone_number="phone_number"
                        :browser_url="browser_url"
                        :browser_content_key="browser_content_key"
                        :mode="phone_mode"
                    >
                        <template #messages>
                            <slot name="phone-messages" />
                        </template>

                        <template #browser>
                            <slot name="phone-browser" />
                        </template>
                    </mock-phone>

                    <p class="demo-note mt-8 mb-0">{{ phone_label }}</p>
                </v-col>
            </v-row>

            <bottom-navigation
                :current_step_number="current_step_number"
                :total_steps="total_steps"
                :progress_percentage="progress_percentage"
                :current_step_name="current_step_name"
                :can_go_previous="can_go_previous"
                :can_go_next="can_go_next"
                @restart="$emit('restart')"
                @previous="$emit('previous')"
                @next="$emit('next')"
            />
        </div>
    </v-app>
</template>

<script>
import BackdropDemo from '@/components/demos/shared/backdrop.vue';
import BottomNavigation from '@/components/demos/shared/bottom-navigation.vue';
import MockPhone from '@/components/demos/shared/mock-phone.vue';
import StepNavigation from '@/components/demos/shared/step-navigation.vue';

export default {
    name: 'BaseDemoFlow',

    components: {
        BackdropDemo,
        BottomNavigation,
        MockPhone,
        StepNavigation
    },

    props: {
        // phone config
        phone_key: {
            type: Number,
            default: 0
        },
        phone_mode: {
            type: String,
            default: 'messages'
        },
        business_name: {
            type: String,
            required: true
        },
        phone_number: {
            type: String,
            required: true
        },
        browser_url: {
            type: String,
            required: true
        },
        phone_label: {
            type: String,
            default: "Customer's Phone"
        },

        // nav state
        current_step_number: {
            type: Number,
            required: true
        },
        total_steps: {
            type: Number,
            required: true
        },
        progress_percentage: {
            type: Number,
            required: true
        },
        current_step_name: {
            type: String,
            default: ''
        },
        can_go_previous: {
            type: Boolean,
            default: false
        },
        can_go_next: {
            type: Boolean,
            default: false
        },
        steps: {
            type: Array,
            default: () => []
        },

        // content animation
        browser_content_key: {
            type: [String, Number],
            default: 'default'
        },

        // misc config
        page_title: {
            type: String,
            default: ''
        }
    },

    emits: ['restart', 'previous', 'next', 'step-click'],

    mounted() {
        if (this.page_title) {
            document.title = this.page_title;
        }
    }
}
</script>

<style lang="scss" scoped>
.demo-wrapper {
    --brand-primary: #40728F;
    --brand-secondary: #e3f2fd;
    --color-text: #666;
    --v-primary-base: var(--brand-primary);
    --v-anchor-base: var(--brand-primary);
    --v-secondary-base: var(--brand-secondary);
    --max-width: 1400px;

    position: relative;
    display: grid;
    place-items: center;
    max-width: var(--max-width);
    margin: 0 auto;
    margin-top: 5rem;
    margin-bottom: 3.5rem;
    width: 100%;
    height: 100%;
}

.content-card {
    position: relative;
    flex-grow: 0;
    max-width: min(1400px, 95%);
    width: 100%;
    padding: 2rem;
    margin: 1rem auto;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 1rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);

    @media (max-width: 900px) {
        padding: 1.5rem .5rem;
    }

    .main-grid {
        max-width: 1100px;
        margin: 2.5rem auto 0 auto;
        display: grid;
        grid-template-columns: 1fr;
        gap: 3rem;
        align-items: flex-start;

        @media (min-width: 900px) {
            grid-template-columns: 1fr 1fr;
            align-items: center;
        }
    }

    .left-side {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        text-align: center;

        @media (min-width: 900px) {
            text-align: left;
        }
    }

    .right-side {
        display: flex;
        flex-direction: column;
        gap: 2rem;
        justify-content: center;
        align-items: center;
    }
}

:deep(.demo-note) {
    color: var(--color-text);
    font-size: 0.875rem;
    font-style: italic;
    text-align: center;
}

:deep(.primary-note) {
    display: flex;
    align-items: flex-start;
    text-align: left;
    gap: 0.5rem;
    background: var(--brand-secondary);
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--brand-secondary);
    max-width: 650px;
    width: 100%;

    span {
        color: var(--color-text);
        font-size: 0.85rem;
        line-height: 1.3;
    }
}

:deep(.explanation-text) {
    text-align: left;
    color: var(--color-text);
    font-size: 1rem;
    max-width: 650px;
    margin: 0 auto;
    padding-top: 0.25rem;

    @media (min-width: 900px) {
        margin: 0;
    }
}

:deep(.explanation-points) {
    max-width: 650px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin: 0 auto;
    list-style-type: none;
    padding-inline-start: 0.5rem;

    @media (min-width: 900px) {
        margin: 0;
    }
}

:deep(.explanation-point) {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.5rem;

    &:last-child {
        margin-bottom: 0;
    }

    .point-icon {
        margin-top: 0.125rem;
        flex-shrink: 0;
    }

    .point-text {
        margin: 0;
        color: var(--color-text);
        font-size: 1rem;
        line-height: 1.5;
        padding-top: 4px;
        text-align: left;
    }
}

.explainer-fade-enter-active,
.explainer-fade-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}

.explainer-fade-enter,
.explainer-fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}
</style>
